import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { listProducts, createProduct, buyProduct, buyProductWithCredits, boostProduct, downloadProduct, deleteProduct, uploadFile, absFileUrl } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useLang } from "../../i18n/LanguageContext";
import { toast } from "sonner";
import { Store, Plus, Loader2, ShoppingCart, Download, Tag, Upload, Check, X, Coins, Zap, Flame, Sparkles, TrendingUp, Trash2 } from "lucide-react";
import { CreatorRow } from "./CreatorRow";

const CATEGORIES = ["E-book", "Template", "Prompt Pack", "Formation", "Design", "Code", "Audio", "Autre"];

const SellModal = ({ open, onClose, onCreated }) => {
  const [f, setF] = useState({ title: "", description: "", price: "", category: "Autre" });
  const [preview, setPreview] = useState(null);
  const [filePath, setFilePath] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [upPrev, setUpPrev] = useState(false);
  const [upFile, setUpFile] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const pickPreview = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 60 * 1024 * 1024) return toast.error("Image trop lourde (max 60 Mo).");
    try { setUpPrev(true); const { data } = await uploadFile(file); setPreview(absFileUrl(data.path)); }
    catch { toast.error("Upload impossible."); } finally { setUpPrev(false); }
  };
  const pickFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 60 * 1024 * 1024) return toast.error("Fichier trop lourd (max 60 Mo).");
    try { setUpFile(true); const { data } = await uploadFile(file); setFilePath(data.path); setFileName(data.filename); }
    catch { toast.error("Upload impossible."); } finally { setUpFile(false); }
  };

  const submit = async () => {
    if (!f.title.trim() || !f.price) return toast.error("Titre et prix requis.");
    try {
      setBusy(true);
      const { data } = await createProduct({ title: f.title, description: f.description, price: parseFloat(f.price), category: f.category, preview, file_path: filePath, file_name: fileName });
      onCreated(data);
      toast.success("Produit publié !");
      onClose();
      setF({ title: "", description: "", price: "", category: "Autre" }); setPreview(null); setFilePath(null); setFileName(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Publication impossible."); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sell-modal" className="max-w-lg border-blue-500/25 bg-[#0B1220]/95 p-8 backdrop-blur-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl font-800 tracking-tighter text-white">Vendre un produit numérique</DialogTitle></DialogHeader>
        <div className="mt-4 space-y-4">
          <input data-testid="sell-title" value={f.title} onChange={set("title")} placeholder="Titre du produit" className="w-full rounded-xl border border-blue-500/20 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40" />
          <textarea data-testid="sell-desc" value={f.description} onChange={set("description")} placeholder="Description" rows={3} className="w-full resize-none rounded-xl border border-blue-500/20 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40" />
          <div className="flex gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-blue-500/20 px-4">
              <Tag size={14} className="text-blue-300" />
              <input data-testid="sell-price" type="number" min="1" step="0.01" value={f.price} onChange={set("price")} placeholder="Prix $" className="w-24 bg-transparent py-3 text-sm text-white outline-none placeholder:text-[#405060]" />
            </div>
            <select data-testid="sell-category" value={f.category} onChange={set("category")} className="flex-1 rounded-xl border border-blue-500/20 bg-[#0B1220] px-4 py-3 text-sm text-white outline-none">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex gap-3">
            <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-blue-500/20 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200 hover:border-blue-400/40">
              {upPrev ? <Loader2 size={14} className="animate-spin" /> : preview ? <Check size={14} className="text-emerald-400" /> : <Upload size={14} />} Aperçu
              <input data-testid="sell-preview-input" type="file" accept="image/*" onChange={pickPreview} className="hidden" />
            </label>
            <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-blue-500/20 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200 hover:border-blue-400/40">
              {upFile ? <Loader2 size={14} className="animate-spin" /> : filePath ? <Check size={14} className="text-emerald-400" /> : <Upload size={14} />} Fichier
              <input data-testid="sell-file-input" type="file" onChange={pickFile} className="hidden" />
            </label>
          </div>
          <button data-testid="sell-submit" onClick={submit} disabled={busy || upPrev || upFile} className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow hover:bg-blue-500 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Publier le produit
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProductCard = ({ p, index, onBuy, onBuyCredits, onDownload, onBoost, onDelete, canManage, pending }) => (
  <motion.div data-testid={`product-${p.id}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: (index % 4) * 0.05 }}
    className={`group flex w-[280px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border backdrop-blur-xl transition-all duration-300 hover:-translate-y-1.5 sm:w-[320px] ${p.boosted ? "border-orange-400/40 bg-orange-400/[0.03] hover:shadow-[0_0_30px_rgba(249,115,22,0.2)]" : "border-blue-500/15 bg-blue-400/[0.02] hover:border-blue-400/50 hover:shadow-[0_0_30px_rgba(37,99,235,0.2)]"}`}>
    <div className="relative aspect-video overflow-hidden bg-blue-500/5">
      {p.preview ? <img src={p.preview} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        : (
          <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.28),transparent_60%),radial-gradient(circle_at_80%_90%,rgba(245,158,11,0.15),transparent_55%)]">
            <div className="pointer-events-none absolute inset-0 opacity-30 grid-lines" />
            <div className="relative flex flex-col items-center gap-2">
              <Store size={30} className="text-blue-300/70" />
              <span className="font-display text-lg font-800 tracking-tighter text-white/85">{p.category}</span>
            </div>
          </div>
        )}
      <span className="absolute left-3 top-3 rounded-full border border-blue-400/30 bg-[#0B1220]/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-blue-200 backdrop-blur-sm">{p.category}</span>
      {p.boosted && (
        <span data-testid={`product-boosted-${p.id}`} className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-orange-400/40 bg-orange-500/20 px-2.5 py-1 font-mono text-[8px] uppercase tracking-[0.15em] text-orange-200">
          <Flame size={9} /> En vedette
        </span>
      )}
      {canManage && (
        <button data-testid={`product-delete-${p.id}`} onClick={() => onDelete(p)}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full border border-red-400/40 bg-[#0B1220]/85 px-2.5 py-1 font-mono text-[8px] uppercase tracking-[0.15em] text-red-300 backdrop-blur-sm transition-colors hover:bg-red-500/25 hover:text-red-100">
          <Trash2 size={10} /> Suppr.
        </button>
      )}
    </div>
    <div className="flex flex-1 flex-col p-5">
      <h3 className="font-display text-lg font-700 tracking-tight text-white">{p.title}</h3>
      <p className="mt-1 line-clamp-2 flex-1 font-body text-xs text-[#8AA0C0]">{p.description}</p>
      <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.15em] text-blue-300/70">par {p.seller_name}</p>
      <div className="mt-4 flex items-center justify-between">
        <div>
          <span className="font-display text-2xl font-800 tracking-tighter text-white">${p.price}</span>
          <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.12em] text-amber-300/80">ou {p.credit_price} cr</span>
        </div>
        {p.owned ? (
          <div className="flex flex-col items-end gap-2">
            <button data-testid={`product-download-${p.id}`} onClick={() => onDownload(p)} className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-200 hover:bg-emerald-400/20">
              <Download size={13} /> Accéder
            </button>
            {!p.boosted && (
              <button data-testid={`product-boost-${p.id}`} onClick={() => onBoost(p)} disabled={pending === `boost-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-orange-400/40 bg-orange-400/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-orange-200 hover:bg-orange-400/20 disabled:opacity-60">
                {pending === `boost-${p.id}` ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />} Booster · 50 cr
              </button>
            )}
          </div>
        ) : p.purchased ? (
          <button data-testid={`product-download-${p.id}`} onClick={() => onDownload(p)} className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-200 hover:bg-emerald-400/20">
            <Download size={13} /> Accéder
          </button>
        ) : (
          <div className="flex flex-col items-end gap-2">
            <button data-testid={`product-buy-${p.id}`} onClick={() => onBuy(p)} disabled={pending === p.id} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white cta-glow hover:bg-blue-500 disabled:opacity-60">
              {pending === p.id ? <Loader2 size={13} className="animate-spin" /> : <ShoppingCart size={13} />} Acheter
            </button>
            <button data-testid={`product-buy-credits-${p.id}`} onClick={() => onBuyCredits(p)} disabled={pending === `cr-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-amber-200 hover:bg-amber-400/20 disabled:opacity-60">
              {pending === `cr-${p.id}` ? <Loader2 size={11} className="animate-spin" /> : <Coins size={11} />} Payer en crédits
            </button>
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

export const Products = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { user, setAuthOpen, setCredits } = useAuth();
  const [products, setProducts] = useState([]);
  const [sellOpen, setSellOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const load = () => listProducts().then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, [user]);

  const onBuy = async (p) => {
    if (!user) return setAuthOpen(true);
    try { setPending(p.id); const { data } = await buyProduct(p.id); if (data.checkout_url) window.location.href = data.checkout_url; }
    catch (e) { setPending(null); toast.error(e.response?.data?.detail || "Achat impossible."); }
  };
  const onBuyCredits = async (p) => {
    if (!user) return setAuthOpen(true);
    try {
      setPending(`cr-${p.id}`);
      const { data } = await buyProductWithCredits(p.id);
      if (typeof data.credits === "number") setCredits(data.credits);
      toast.success("Produit acheté avec vos crédits !");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" && d) || "Achat impossible.");
    } finally { setPending(null); }
  };
  const onBoost = async (p) => {
    try {
      setPending(`boost-${p.id}`);
      const { data } = await boostProduct(p.id);
      if (typeof data.credits === "number") setCredits(data.credits);
      toast.success("Produit mis en vedette pendant 24h !");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" && d) || "Boost impossible.");
    } finally { setPending(null); }
  };
  const onDownload = async (p) => {
    try {
      const res = await downloadProduct(p.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = p.title; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      if (e.response?.status === 404) toast.info("Aucun fichier joint à ce produit.");
      else toast.error("Accès impossible.");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Supprimer définitivement « ${p.title} » ?`)) return;
    try {
      await deleteProduct(p.id);
      setProducts((ps) => ps.filter((x) => x.id !== p.id));
      toast.success("Produit supprimé.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible.");
    }
  };

  const renderCard = (p, i) => (
    <ProductCard key={p.id} p={p} index={i} onBuy={onBuy} onBuyCredits={onBuyCredits} onDownload={onDownload} onBoost={onBoost}
      onDelete={onDelete} canManage={p.owned || user?.role === "admin"} pending={pending} />
  );
  const boosted = products.filter((p) => p.boosted);
  const byCategory = CATEGORIES
    .map((c) => ({ category: c, items: products.filter((p) => p.category === c) }))
    .filter((g) => g.items.length > 0);

  return (
    <section id="products" data-testid="products-section" className="relative py-24 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(37,99,235,0.08),transparent_55%)]" />

      {/* CREATE. UPLOAD. EARN. hero band */}
      <div ref={ref} className="relative mx-auto max-w-[1600px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-3xl border border-blue-500/20 bg-[#0B1220]/60 px-7 py-14 text-center backdrop-blur-xl sm:px-10 sm:py-20"
        >
          <div className="pointer-events-none absolute inset-0 opacity-40 grid-lines" />
          <div className="pointer-events-none absolute -left-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-blue-600/20 blur-[90px]" />
          <div className="pointer-events-none absolute -right-24 top-1/3 h-72 w-72 rounded-full bg-amber-500/15 blur-[90px]" />
          <div className="relative">
            <span className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">[ {t("products.tag")} ]</span>
            <h2 className="mt-5 font-display text-4xl font-800 leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
              <span className="earn-gradient">CREATE. UPLOAD. EARN.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-2xl font-body text-base leading-relaxed text-[#CBD5E1] sm:text-lg">
              {t("products.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button data-testid="sell-open" onClick={() => (user ? setSellOpen(true) : setAuthOpen(true))}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:scale-[1.02] hover:bg-blue-500">
                <Plus size={15} /> {t("products.sell")}
              </button>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/[0.06] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-300">
                <Coins size={13} /> Gardez 80% de chaque vente
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Category rows */}
      <div className="relative mx-auto mt-8 max-w-[1600px] pl-6 lg:pl-10" data-testid="products-rows">
        {products.length === 0 ? (
          <div className="mr-6 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-blue-500/30 bg-blue-400/[0.02] py-20 text-center lg:mr-10">
            <Store size={44} className="text-blue-400/40" />
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8AA0C0]">{t("products.empty")}</p>
            <button data-testid="products-empty-sell" onClick={() => (user ? setSellOpen(true) : setAuthOpen(true))}
              className="mt-1 inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow hover:bg-blue-500">
              <Plus size={15} /> {t("products.sell")}
            </button>
          </div>
        ) : (
          <>
            {boosted.length > 0 && (
              <CreatorRow title="En vedette" icon={Flame} accent="amber" testid="products-row-boosted" count={boosted.length}>
                {boosted.map(renderCard)}
              </CreatorRow>
            )}
            <CreatorRow title="Nouveautés" icon={Sparkles} testid="products-row-new" count={products.length}>
              {products.map(renderCard)}
            </CreatorRow>
            {byCategory.map((g) => (
              <CreatorRow key={g.category} title={g.category} icon={TrendingUp} testid={`products-row-${g.category}`} count={g.items.length}>
                {g.items.map(renderCard)}
              </CreatorRow>
            ))}
          </>
        )}
      </div>

      <SellModal open={sellOpen} onClose={() => setSellOpen(false)} onCreated={(p) => setProducts((ps) => [p, ...ps])} />
    </section>
  );
};
